import { useState } from 'react';
import { AlertTriangle, BadgeCheck, RefreshCw } from 'lucide-react';
import type { CharityVerificationDecided, CharityVerificationQueueItem } from '../api/adminOps';
import { DetailDrawer, QueueTable, StatusChip, type QueueColumn } from '../components/ui';
import {
  useCharityVerification,
  useCharityVerifications,
  type CharityVerificationDecisionInput,
} from '../hooks/useAdminOps';
import {
  ALL_FILTER_HINT,
  ALL_STATUSES,
  claimantLabel,
  contactStateChip,
  emptyHint,
  filterLabel,
  formatAge,
  queueParams,
  READ_ONLY_DECISION_TEXT,
  severityChip,
  STATUS_FILTERS,
  statusChip,
  verdictChip,
  type StatusFilter,
} from './charityVerification.logic';
import {
  decisionFeedback,
  PENDING_SECOND_OPERATOR_FEEDBACK,
  type DecisionFeedback,
} from './charityVerificationDecision.logic';
import CharityVerificationCaseView from './CharityVerificationCaseView';
import CharityVerificationDecisionForm from './CharityVerificationDecisionForm';
import CharityVerificationEvidence from './CharityVerificationEvidence';

export const DECISIONS_NOT_WIRED_TEXT = 'Verification decisions are not wired on this route yet.';

/** What the route hands the mutation so the drawer can react to the answer. */
export interface DecisionHandlers {
  onSuccess: (result: { data: CharityVerificationDecided }) => void;
  onError: (error: Error) => void;
}

interface CharityVerificationProps {
  /** Wired by the route to the decideCharityVerification mutation hook. */
  onDecision?: (input: CharityVerificationDecisionInput, handlers: DecisionHandlers) => void;
  isDecisionPending?: boolean;
  /** Reads are for every admin group; a decision for admin and admin-operator. */
  canDecide?: boolean;
}

const FEEDBACK_CLASSES: Record<DecisionFeedback['tone'], string> = {
  ok: 'bg-green-500/10 border-green-500/30 text-green-300',
  warn: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-300',
  crit: 'bg-red-500/10 border-red-500/30 text-red-300',
  info: 'bg-primary/10 border-primary/30 text-primary',
  neutral: 'bg-gray-500/10 border-gray-border text-gray-text',
};

function ErrorCard({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div className="glass-card p-6 border border-red-500/30 flex items-start gap-3" role="alert">
      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div>
        <p className="text-white font-medium mb-1">{title}</p>
        <p className="text-sm text-gray-text mb-3">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <button type="button" onClick={onRetry} className="btn-secondary">
          Retry
        </button>
      </div>
    </div>
  );
}

function YesOrDash({ value, label }: { value: boolean; label: string }) {
  return value ? (
    <StatusChip label={label} tone="warn" />
  ) : (
    <span className="text-gray-text">—</span>
  );
}

export default function CharityVerification({
  onDecision,
  isDecisionPending,
  canDecide = true,
}: CharityVerificationProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(ALL_STATUSES);
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<DecisionFeedback | null>(null);

  const queue = useCharityVerifications(queueParams(statusFilter));
  const selectedRow = queue.rows.find(row => row.claim_id === selectedClaimId) ?? null;
  const detail = useCharityVerification(selectedClaimId);

  const openClaim = (claimId: string) => {
    setFeedback(null);
    setSelectedClaimId(claimId);
  };
  const closeDrawer = () => {
    setFeedback(null);
    setSelectedClaimId(null);
  };

  const handlers: DecisionHandlers = {
    onSuccess: result => {
      if (result.data.pending_second_operator) setFeedback(PENDING_SECOND_OPERATOR_FEEDBACK);
      else closeDrawer();
    },
    onError: error => {
      const next = decisionFeedback(error);
      setFeedback(next);
      // 409 CLAIM_NOT_IN_STATE: the claim moved under the reviewer, so the
      // detail (and its allowed_actions) is read again before anything else.
      if (next.refreshClaim) void detail.refetch();
    },
  };

  const columns: QueueColumn<CharityVerificationQueueItem>[] = [
    {
      key: 'organization',
      header: 'Organization',
      render: row => (
        <div className="min-w-0">
          <div className="font-medium">{row.organization.name}</div>
          <div className="text-xs text-gray-text">
            <span className="font-mono">{row.organization.ein}</span>
            {row.organization.state !== null && ` · ${row.organization.state}`}
          </div>
        </div>
      ),
    },
    {
      key: 'claimant',
      header: 'Claimant',
      render: row => (
        <div className="min-w-0">
          <div>{claimantLabel(row.claimant.display_name, row.organization.name)}</div>
          <div className="text-xs text-gray-text">@{row.claimant.email_domain}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: row => {
        const chip = statusChip(row.status);
        return <StatusChip label={chip.label} tone={chip.tone} />;
      },
    },
    {
      key: 'risk',
      header: 'Risk',
      render: row => {
        const severity = severityChip(row.highest_flag_severity);
        return (
          <div className="flex items-center gap-2">
            <span className="tabular-nums">{row.risk_score}</span>
            <StatusChip label={severity.label} tone={severity.tone} />
          </div>
        );
      },
    },
    {
      key: 'disputed',
      header: 'Disputed',
      render: row => <YesOrDash value={row.disputed} label="Disputed" />,
    },
    {
      key: 'second-operator',
      header: '2nd operator',
      render: row => <YesOrDash value={row.second_operator_required} label="Required" />,
    },
    {
      key: 'age',
      header: 'Age',
      render: row => (
        <span className="whitespace-nowrap text-gray-text">{formatAge(row.age_hours)}</span>
      ),
    },
    {
      key: 'documents',
      header: 'Documents',
      render: row => <span className="tabular-nums">{row.documents_count}</span>,
    },
    {
      key: 'contact',
      header: 'Contact',
      render: row => {
        const chip = contactStateChip(row.contact_confirmation_state);
        return <StatusChip label={chip.label} tone={chip.tone} />;
      },
    },
    {
      key: 'verdict',
      header: 'Latest verdict',
      render: row => {
        if (row.latest_verdict === null) return <span className="text-gray-text">—</span>;
        const chip = verdictChip(row.latest_verdict.verdict, row.latest_verdict.mode);
        return <StatusChip label={chip.label} tone={chip.tone} />;
      },
    },
    {
      key: 'sampled',
      header: 'Sampled',
      render: row =>
        row.sampled_for_review ? (
          <StatusChip label="Sampled" tone="info" />
        ) : (
          <span className="text-gray-text">—</span>
        ),
    },
  ];

  const now = Date.now();

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
            <BadgeCheck className="w-5 h-5 text-primary" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Charity Verification</h1>
            <p className="text-sm text-gray-text">
              Claims on IRS organizations awaiting a reviewer. Every read and every decision is
              recorded in the audit ledger.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-secondary flex items-center space-x-2"
          onClick={() => queue.refetch()}
          disabled={queue.isFetching}
        >
          <RefreshCw className={`w-4 h-4 ${queue.isFetching ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      <div className="glass-card p-4 space-y-2">
        <div
          className="flex items-center gap-2 flex-wrap"
          role="group"
          aria-label="Filter by status"
        >
          <span className="text-sm text-gray-text mr-2">Status:</span>
          {STATUS_FILTERS.map(value => (
            <button
              type="button"
              key={value}
              aria-pressed={statusFilter === value}
              onClick={() => {
                setStatusFilter(value);
                closeDrawer();
              }}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                statusFilter === value
                  ? 'bg-primary/20 text-primary'
                  : 'bg-gray-500/10 text-gray-text hover:text-white'
              }`}
            >
              {filterLabel(value)}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-text">{ALL_FILTER_HINT}</p>
      </div>

      {queue.isError ? (
        <ErrorCard
          title="Failed to load the verification queue"
          error={queue.error}
          onRetry={() => queue.refetch()}
        />
      ) : (
        <QueueTable
          columns={columns}
          rows={queue.rows}
          rowKey={row => row.claim_id}
          isLoading={queue.isPending}
          onRowClick={row => openClaim(row.claim_id)}
          rowActionLabel={row => `Open claim for ${row.organization.name}`}
          emptyTitle="No claims to review"
          emptyHint={emptyHint(statusFilter)}
          hasNextPage={queue.hasNextPage}
          isFetchingNextPage={queue.isFetchingNextPage}
          onLoadMore={() => queue.fetchNextPage()}
        />
      )}

      <DetailDrawer
        open={selectedClaimId !== null}
        title={selectedRow === null ? 'Claim' : `Claim — ${selectedRow.organization.name}`}
        onClose={closeDrawer}
        footer={
          feedback !== null && (
            <div
              role="status"
              className={`p-3 rounded-lg border text-sm ${FEEDBACK_CLASSES[feedback.tone]}`}
            >
              <p className="font-medium">{feedback.title}</p>
              <p>{feedback.message}</p>
            </div>
          )
        }
      >
        {detail.isPending && <p className="text-sm text-gray-text">Loading claim…</p>}

        {detail.isError && (
          <ErrorCard
            title="Could not load this claim"
            error={detail.error}
            onRetry={() => detail.refetch()}
          />
        )}

        {detail.isSuccess && (
          <>
            <CharityVerificationCaseView detail={detail.data.data} row={selectedRow} />
            <CharityVerificationEvidence
              documents={detail.data.data.documents}
              now={now}
              onRefetch={() => detail.refetch()}
              extractions={detail.data.data.extractions}
              checks={detail.data.data.checks}
              verdicts={detail.data.data.verdicts}
              disputes={detail.data.data.disputes}
            />
            {!canDecide ? (
              <p className="text-sm text-gray-text border-t border-gray-border pt-4">
                {READ_ONLY_DECISION_TEXT}
              </p>
            ) : onDecision === undefined ? (
              <p className="text-sm text-gray-text border-t border-gray-border pt-4">
                {DECISIONS_NOT_WIRED_TEXT}
              </p>
            ) : (
              <div className="border-t border-gray-border pt-4">
                <CharityVerificationDecisionForm
                  key={detail.data.data.claim.id}
                  detail={detail.data.data}
                  isPending={isDecisionPending}
                  onSubmit={payload =>
                    onDecision({ claimId: detail.data.data.claim.id, payload }, handlers)
                  }
                />
              </div>
            )}
          </>
        )}
      </DetailDrawer>
    </div>
  );
}
